import { forwardRef, type InputHTMLAttributes, type ReactNode, useId } from 'react';

import { cn } from './cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, leftIcon, rightIcon, className, wrapperClassName, id, ...props },
  ref
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const errorId = error ? `${inputId}-error` : undefined;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('w-full', wrapperClassName)}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-semibold text-brand-dark mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span
            aria-hidden="true"
            className="absolute inset-y-0 left-0 pl-3 flex items-center text-brand-gray pointer-events-none"
          >
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={describedBy}
          className={cn(
            'w-full bg-white border rounded-xl px-4 py-2.5 text-brand-dark text-sm transition-colors',
            'placeholder:text-brand-light',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tomato/30 focus-visible:border-brand-tomato',
            'disabled:bg-brand-ghost disabled:cursor-not-allowed',
            leftIcon && 'pl-10',
            rightIcon && 'pr-10',
            error
              ? 'border-red-400 focus-visible:border-red-500 focus-visible:ring-red-500/30'
              : 'border-brand-silver',
            className
          )}
          {...props}
        />
        {rightIcon && (
          <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-brand-gray">
            {rightIcon}
          </span>
        )}
      </div>
      {hint && !error && (
        <p id={hintId} className="text-xs text-brand-gray mt-1">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-red-600 mt-1 font-medium">
          {error}
        </p>
      )}
    </div>
  );
});

export default Input;
